import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Cpu, MemoryStick, HardDrive, Zap, CircleDot, CircleOff, Search, X, AlertTriangle,
} from 'lucide-react';
import { metrics as api } from '../api';
import type { WirtUebersicht } from '../api';

/**
 * Alle Server auf einen Blick: Last, Speicher, Platte, Erreichbarkeit.
 *
 * ══ WAS „ONLINE" HIER HEISST ═══════════════════════════════════════════════
 *
 * Dass Messwerte eintreffen — nicht, was in der Serverliste steht. Ein Server
 * ohne Agenten (oder mit einem, der nichts mehr schickt) erscheint als
 * offline, auch wenn er READY ist. Genau das ist die Frage, die jemand hat,
 * der hierher schaut: meldet sich die Maschine noch.
 *
 * ══ WARUM BALKEN UND KEINE ACHT DIAGRAMME ══════════════════════════════════
 *
 * Diese Seite beantwortet „ist alles in Ordnung", nicht „was war um 3 Uhr".
 * Dafür reicht je Kennzahl ein Balken mit Zahl — und für die Last eine
 * Sparkline, weil eine Spitze vor zehn Minuten etwas anderes bedeutet als
 * dauerhafte Volllast. Den Verlauf im Einzelnen zeigt der Reiter „Verläufe"
 * des jeweiligen Servers; dorthin führt jede Karte.
 */

function gb(bytes: number | null): string {
  if (bytes === null) return '—';
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

/** Ab wann eine Auslastung Aufmerksamkeit verdient. */
function farbe(prozent: number | null): string {
  if (prozent === null) return 'bg-slate-200';
  if (prozent >= 90) return 'bg-red-500';
  if (prozent >= 75) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function Balken({
  icon,
  label,
  prozent,
  zusatz,
}: {
  icon: React.ReactNode;
  label: string;
  prozent: number | null;
  zusatz?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1.5 text-slate-500">
          {icon} {label}
        </span>
        <span className="font-mono tabular-nums text-slate-700">
          {prozent === null ? '—' : `${prozent.toFixed(0)} %`}
          {zusatz && <span className="ml-1.5 text-slate-400">{zusatz}</span>}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${farbe(prozent)}`}
          style={{ width: `${Math.min(100, Math.max(0, prozent ?? 0))}%` }}
        />
      </div>
    </div>
  );
}

/** Der Lastverlauf der letzten Stunde, als Fläche. */
function Sparkline({ werte }: { werte: number[] }) {
  if (werte.length < 2) {
    return <div className="h-8 rounded bg-slate-50" />;
  }
  const breite = 100;
  const hoehe = 28;
  const max = Math.max(20, ...werte);
  const punkte = werte.map((v, i) => {
    const x = (i / (werte.length - 1)) * breite;
    const y = hoehe - (Math.max(0, v) / max) * hoehe;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  return (
    <svg viewBox={`0 0 ${breite} ${hoehe}`} preserveAspectRatio="none" className="h-8 w-full">
      <polyline
        points={`0,${hoehe} ${punkte.join(' ')} ${breite},${hoehe}`}
        className="fill-brand-500/10"
      />
      <polyline
        points={punkte.join(' ')}
        fill="none"
        className="stroke-brand-500"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function Karte({ w }: { w: WirtUebersicht }) {
  const ramProzent =
    w.ramBelegtBytes !== null && w.ramGesamtBytes ? (w.ramBelegtBytes / w.ramGesamtBytes) * 100 : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={`/servers?server=${w.serverId}`}
            className="truncate font-mono text-sm font-medium text-slate-800 hover:text-brand-700"
          >
            {w.hostname}
          </Link>
          <p className="truncate font-mono text-[11px] text-slate-400">{w.ipAddress}</p>
          <p className="mt-0.5 truncate text-[11px] text-slate-400">
            {w.provider}
            {w.kunde && <span className="text-slate-500"> · {w.kunde}</span>}
          </p>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            w.online ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
          title={
            w.online
              ? 'Es treffen Messwerte ein.'
              : 'Seit über einer Stunde keine Messwerte — läuft dort ein vmagent?'
          }
        >
          {w.online ? <CircleDot size={10} /> : <CircleOff size={10} />}
          {w.online ? 'meldet sich' : 'still'}
        </span>
      </div>

      {w.online ? (
        <>
          <div className="mt-3">
            <Sparkline werte={w.cpuVerlauf} />
          </div>
          <div className="mt-2 space-y-2.5">
            <Balken icon={<Cpu size={11} />} label="Prozessor" prozent={w.cpuProzent} />
            {/*
              Entzogene Rechenzeit nur zeigen, wenn sie ins Gewicht faellt.
              Ein, zwei Prozent sind auf jedem geteilten Wirt normal und
              wuerden als Dauerhinweis nur abstumpfen. Ab 15 % lohnt der
              Blick, ab 30 % ist die Maschine spuerbar ausgebremst.

              Der Balken darueber zeigt die EIGENLAST — steal ist dort
              herausgerechnet. Sonst saehe eine gedrosselte Maschine aus wie
              eine ausgelastete, und die beiden brauchen gegensaetzliche
              Reaktionen.
            */}
            {typeof w.stealProzent === 'number' && w.stealProzent >= 15 && (
              <div
                className={`mt-0.5 flex items-center gap-1 text-[11px] ${
                  w.stealProzent >= 30 ? 'text-red-600' : 'text-amber-600'
                }`}
                title="Rechenzeit, die der Anbieter der Maschine entzieht. Die Maschine ist nicht ausgelastet, sondern ausgebremst — dagegen hilft nur, die Drosselung beim Anbieter aufheben zu lassen."
              >
                <AlertTriangle size={10} className="shrink-0" />
                <span>
                  {Math.round(w.stealProzent)} % vom Anbieter entzogen
                  {w.stealProzent >= 30 ? ' — die Maschine wird gedrosselt' : ''}
                </span>
              </div>
            )}
            <Balken
              icon={<MemoryStick size={11} />}
              label="Arbeitsspeicher"
              prozent={ramProzent}
              zusatz={
                w.ramBelegtBytes !== null ? `${gb(w.ramBelegtBytes)} / ${gb(w.ramGesamtBytes)}` : undefined
              }
            />
            <Balken icon={<HardDrive size={11} />} label="Platte" prozent={w.plattenProzent} />
          </div>
        </>
      ) : (
        <p className="mt-3 text-[11px] leading-snug text-slate-400">
          Keine Messwerte. Entweder läuft dort kein <code className="font-mono">vmagent</code>,
          oder er erreicht die Metrik nicht.
        </p>
      )}

      {/* GPU steht in der Serververwaltung, gemessen wird sie nicht: es läuft
          kein nvidia-Exporter. Lieber die bekannte Ausstattung zeigen als eine
          Auslastung, die niemand erhebt. */}
      {w.gpu && (
        <div className="mt-3 flex items-start gap-1.5 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          <Zap size={11} className="mt-0.5 shrink-0 text-amber-500" />
          <span>
            {w.gpu.anzahl && w.gpu.anzahl > 1 ? `${w.gpu.anzahl} × ` : ''}
            {w.gpu.modell ?? 'GPU'}
            {w.gpu.vramMb ? ` · ${(w.gpu.vramMb / 1024).toFixed(0)} GB VRAM` : ''}
            <span className="block text-slate-400">
              Ausstattung laut Serververwaltung — Auslastung wird nicht gemessen.
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

export default function MonitoringPanel() {
  const [suche, setSuche] = useState('');
  const [anbieterFilter, setAnbieterFilter] = useState('');
  const [kundeFilter, setKundeFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['metrik-wirte'],
    queryFn: () => api.wirte(),
    // Die Seite bleibt gern offen stehen. Eine Minute ist oft genug, um eine
    // Auslastung zu bemerken, und selten genug, um die Metrik nicht zu fluten.
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );
  }

  if (!data?.verfuegbar) {
    return (
      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        {data?.grund ?? 'Die Messwerte sind nicht erreichbar.'}
      </p>
    );
  }

  const stille = data.wirte.filter((w) => !w.online).length;

  // Die Auswahlfelder kommen aus den Daten, nicht aus einer festen Liste: ein
  // neuer Anbieter oder Kunde soll erscheinen, ohne dass hier jemand nachzieht.
  const anbieter = [...new Set(data.wirte.map((w) => w.provider).filter(Boolean))].sort();
  const kunden = [...new Set(data.wirte.map((w) => w.kunde).filter((k): k is string => Boolean(k)))].sort();

  const gefiltert = data.wirte.filter((w) => {
    if (suche && !`${w.hostname} ${w.ipAddress}`.toLowerCase().includes(suche.toLowerCase())) {
      return false;
    }
    if (anbieterFilter && w.provider !== anbieterFilter) return false;
    // „Unsere" heisst: kein Kunde hinterlegt. Das ist die Frage, die man
    // tatsaechlich hat — nicht „welcher Kunde ist es nicht".
    if (kundeFilter === '__eigene' && w.kunde) return false;
    if (kundeFilter && kundeFilter !== '__eigene' && w.kunde !== kundeFilter) return false;
    return true;
  });

  const filterAktiv = Boolean(suche || anbieterFilter || kundeFilter);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Name oder Adresse"
            className="w-56 rounded-lg border border-slate-300 py-1.5 pl-8 pr-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <select
          value={anbieterFilter}
          onChange={(e) => setAnbieterFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
        >
          <option value="">Alle Anbieter</option>
          {anbieter.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          value={kundeFilter}
          onChange={(e) => setKundeFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
        >
          <option value="">Alle Server</option>
          <option value="__eigene">Nur unsere eigenen</option>
          {kunden.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
        {filterAktiv && (
          <button
            onClick={() => { setSuche(''); setAnbieterFilter(''); setKundeFilter(''); }}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
          >
            <X size={12} /> Filter zurücksetzen
          </button>
        )}
      </div>

      <p className="text-xs text-slate-500">
        {filterAktiv
          ? `${gefiltert.length} von ${data.wirte.length} Servern`
          : `${data.wirte.length} Server`}
        {' · '}
        {data.wirte.length - stille} melden sich
        {stille > 0 && <span className="text-slate-400"> · {stille} still</span>}
        <span className="ml-2 text-slate-400">Aktualisiert sich jede Minute.</span>
      </p>

      {gefiltert.length === 0 ? (
        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-xs text-slate-500">
          Kein Server passt zu diesen Filtern.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {gefiltert.map((w) => (
            <Karte key={w.serverId} w={w} />
          ))}
        </div>
      )}
    </div>
  );
}
